/* Ashloé theme behaviour: header, reveal motion, cart drawer, gallery, UGC. */
(() => {
  const routes = () => window.ashloeRoutes || {};
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  /* ------------------------------------------------------------ reveal */
  const io = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) { e.target.classList.add('is-visible'); io.unobserve(e.target); }
        });
      }, { rootMargin: '0px 0px -10% 0px' })
    : null;
  const observeReveals = (root = document) => {
    $$('.reveal, [data-reveal]', root).forEach((el) => (io ? io.observe(el) : el.classList.add('is-visible')));
  };

  /* ------------------------------------------------------- scroll bar */
  const progress = () => {
    const bar = $('[data-scroll-progress]');
    if (!bar) return;
    const set = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      bar.style.width = `${h > 0 ? Math.min(100, (window.scrollY / h) * 100) : 0}%`;
    };
    set();
    window.addEventListener('scroll', set, { passive: true });
    window.addEventListener('resize', set);
  };

  /* -------------------------------------------------------- mobile nav */
  const mobileNav = () => {
    const btn = $('[data-menu-toggle]');
    const menu = $('[data-mobile-menu]');
    if (!btn || !menu) return;
    const setOpen = (open) => {
      menu.classList.toggle('is-open', open);
      btn.setAttribute('aria-expanded', String(open));
    };
    btn.addEventListener('click', () => setOpen(!menu.classList.contains('is-open')));
    menu.addEventListener('click', (e) => { if (e.target.closest('a')) setOpen(false); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') setOpen(false); });
    window.addEventListener('resize', () => { if (window.innerWidth >= 900) setOpen(false); });
  };

  /* -------------------------------------------------------- cart drawer */
  const drawer = {
    el: null,
    open() { if (this.el) { this.el.classList.add('is-open'); document.body.style.overflow = 'hidden'; } },
    close() { if (this.el) { this.el.classList.remove('is-open'); document.body.style.overflow = ''; } },
    async refresh() {
      const res = await fetch(`${routes().cart}?section_id=cart-drawer`);
      const html = await res.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const next = parsed.querySelector('[data-cart-contents]');
      const current = $('[data-cart-contents]');
      if (next && current) current.innerHTML = next.innerHTML;
      const count = parsed.querySelector('[data-cart-total-count]');
      $$('[data-cart-count]').forEach((node) => {
        const value = count ? count.textContent.trim() : '0';
        node.textContent = value;
        node.hidden = value === '0';
      });
    }
  };

  const showFormError = (form, message) => {
    let note = form.querySelector('[data-form-error]');
    if (!note) {
      note = document.createElement('p');
      note.className = 'form-note form-note--error';
      note.setAttribute('data-form-error', '');
      note.setAttribute('role', 'alert');
      note.style.marginTop = '10px';
      form.appendChild(note);
    }
    note.textContent = message;
  };
  const clearFormError = (form) => {
    const note = form.querySelector('[data-form-error]');
    if (note) note.remove();
  };

  const bindCart = () => {
    drawer.el = $('[data-cart-drawer]');
    document.addEventListener('click', async (event) => {
      const openBtn = event.target.closest('[data-cart-open]');
      if (openBtn) { event.preventDefault(); drawer.open(); return; }
      if (event.target.closest('[data-cart-close]')) { drawer.close(); return; }

      const change = event.target.closest('[data-line-change]');
      if (change) {
        event.preventDefault();
        change.disabled = true;
        await fetch(routes().cartChange, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: change.dataset.lineKey, quantity: Number(change.dataset.lineChange) })
        });
        await drawer.refresh();
      }
    });

    document.addEventListener('submit', async (event) => {
      const form = event.target.closest('form[data-product-form]');
      if (!form) return;
      event.preventDefault();
      const submit = form.querySelector('[type="submit"]');
      if (submit) submit.disabled = true;
      const body = new FormData(form);
      let res;
      try {
        res = await fetch(routes().cartAdd, { method: 'POST', headers: { Accept: 'application/json' }, body });
      } catch (err) {
        if (submit) submit.disabled = false;
        showFormError(form, 'Something went wrong. Please try again.');
        return;
      }
      if (submit) submit.disabled = false;
      if (!res.ok) {
        let message = 'This item could not be added to your cart.';
        try { const data = await res.json(); if (data && data.description) message = data.description; } catch (e) {}
        showFormError(form, message);
        return;
      }
      clearFormError(form);
      await drawer.refresh();
      if (form.dataset.buyNow === 'true') { window.location.href = routes().checkout || '/checkout'; return; }
      drawer.open();
    });

    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') drawer.close(); });
  };

  /* ----------------------------------------------------------- gallery */
  const gallery = () => {
    $$('[data-gallery]').forEach((root) => {
      const track = $('[data-gallery-track]', root);
      const counter = $('[data-gallery-counter]', root);
      const thumbs = $$('[data-gallery-thumb]', root);
      if (!track) return;
      const slides = $$('.gallery__slide', track);

      const sync = () => {
        const index = Math.round(track.scrollLeft / track.clientWidth);
        if (counter) counter.textContent = `${Math.min(index + 1, slides.length)}/${slides.length}`;
        thumbs.forEach((t, i) => t.setAttribute('aria-current', String(i === index)));
      };
      track.addEventListener('scroll', () => window.requestAnimationFrame(sync), { passive: true });
      thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => {
        track.scrollTo({ left: i * track.clientWidth, behavior: 'smooth' });
      }));
      sync();
    });
  };

  /* ----------------------------------------------------------- variants */
  const variants = () => {
    $$('[data-variant-picker]').forEach((picker) => {
      const jsonEl = $('[data-variant-json]', picker);
      if (!jsonEl || picker.dataset.bound === 'true') return;
      let data;
      try { data = JSON.parse(jsonEl.textContent); } catch (err) { return; }
      picker.dataset.bound = 'true';

      const apply = () => {
        const chosen = $$('input:checked', picker).map((i) => i.value);
        const match = data.find((v) => v.options.length === chosen.length && v.options.every((opt, i) => opt === chosen[i]));
        if (!match) return;
        // Keep every add-to-cart entry point (main, buy-now, sticky bar) in sync
        // with the selected variant so we never add the wrong one.
        $$('form[data-product-form]').forEach((form) => {
          const idInput = form.querySelector('[name="id"]');
          if (idInput) idInput.value = match.id;
          const submit = form.querySelector('[type="submit"]');
          if (submit) {
            submit.disabled = !match.available;
            if (submit.dataset.labelAvailable) {
              submit.textContent = match.available
                ? submit.dataset.labelAvailable
                : (submit.dataset.labelSoldOut || 'Sold out');
            }
          }
        });
        $$('[data-variant-price]').forEach((node) => {
          let html = `<span>${match.price}</span>`;
          if (match.compare_at) html += `<span class="price--compare">${match.compare_at}</span>`;
          node.innerHTML = html;
        });
        const url = new URL(window.location.href);
        url.searchParams.set('variant', match.id);
        window.history.replaceState({}, '', url);
      };

      picker.addEventListener('change', apply);
    });
  };

  /* --------------------------------------------------------------- UGC */
  const ugcObservers = new WeakMap();
  const ugc = () => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const loadSources = (video) => {
      if (video.dataset.srcLoaded === 'true') return;
      let changed = false;
      $$('source[data-src]', video).forEach((s) => {
        if (!s.src) { s.src = s.dataset.src; changed = true; }
      });
      if (changed) { video.dataset.srcLoaded = 'true'; video.load(); }
    };

    $$('.ugc__item').forEach((item) => {
      const video = $('[data-ugc-video]', item);
      if (!video || ugcObservers.get(item)) return;

      video.muted = true;
      item.dataset.muted = 'true';
      item.dataset.playing = 'false';
      item.dataset.userPaused = 'false';

      const play = () => {
        if (reduced || item.dataset.userPaused === 'true') return;
        loadSources(video);
        const p = video.play();
        if (p && p.catch) p.catch(() => {});
      };
      const pause = () => { if (!video.paused) video.pause(); };

      video.addEventListener('playing', () => { item.dataset.playing = 'true'; });
      video.addEventListener('pause', () => { item.dataset.playing = 'false'; });

      const muteBtn = $('[data-ugc-mute]', item);
      if (muteBtn) muteBtn.addEventListener('click', () => {
        video.muted = !video.muted;
        item.dataset.muted = String(video.muted);
        muteBtn.setAttribute('aria-pressed', String(video.muted));
        muteBtn.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
        if (!video.muted) play();
      });

      const toggleBtn = $('[data-ugc-toggle]', item);
      if (toggleBtn) toggleBtn.addEventListener('click', () => {
        if (video.paused) { item.dataset.userPaused = 'false'; play(); }
        else { item.dataset.userPaused = 'true'; pause(); }
        toggleBtn.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
      });

      if (!('IntersectionObserver' in window)) { loadSources(video); return; }

      // Preload sources slightly before the card enters the viewport.
      const preloader = new IntersectionObserver((entries) => {
        entries.forEach((e) => { if (e.isIntersecting) { loadSources(video); preloader.unobserve(e.target); } });
      }, { rootMargin: '200px' });
      preloader.observe(item);

      const playback = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.intersectionRatio >= 0.6) play();
          else { pause(); if (!video.muted) { video.muted = true; item.dataset.muted = 'true'; } }
        });
      }, { threshold: [0, 0.6] });
      playback.observe(item);

      ugcObservers.set(item, true);
    });
  };


  const init = () => { observeReveals(); progress(); mobileNav(); bindCart(); gallery(); variants(); ugc(); };

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);

  /* Theme editor support */
  document.addEventListener('shopify:section:load', (e) => { observeReveals(e.target); gallery(); variants(); ugc(); });
})();

/* ------------------------------------------------------- lifestyle slider */
(function () {
  function initSlider(root) {
    var track = root.querySelector('[data-slider-track]');
    if (!track) return;
    var slides = Array.prototype.slice.call(track.querySelectorAll('[data-slide]'));
    if (slides.length < 2) return;
    var dotsWrap = root.querySelector('[data-slider-dots]');
    var index = 0;
    var dots = [];

    function go(i, smooth) {
      index = (i + slides.length) % slides.length;
      track.scrollTo({ left: slides[index].offsetLeft, behavior: smooth === false ? 'auto' : 'smooth' });
      sync();
    }
    function sync() {
      dots.forEach(function (d, i) { d.setAttribute('aria-current', i === index ? 'true' : 'false'); });
    }
    if (dotsWrap) {
      slides.forEach(function (_, i) {
        var b = document.createElement('button');
        b.type = 'button';
        b.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        b.addEventListener('click', function () { go(i); });
        dotsWrap.appendChild(b);
        dots.push(b);
      });
    }
    var prev = root.querySelector('[data-slider-prev]');
    var next = root.querySelector('[data-slider-next]');
    if (prev) prev.addEventListener('click', function () { go(index - 1); });
    if (next) next.addEventListener('click', function () { go(index + 1); });

    var raf;
    track.addEventListener('scroll', function () {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(function () {
        var mid = track.scrollLeft + track.clientWidth / 2;
        var closest = 0, best = Infinity;
        slides.forEach(function (s, i) {
          var d = Math.abs(s.offsetLeft + s.offsetWidth / 2 - mid);
          if (d < best) { best = d; closest = i; }
        });
        index = closest;
        sync();
      });
    }, { passive: true });

    sync();

    if (root.dataset.autoplay === 'true') {
      var interval = parseInt(root.dataset.interval, 10) || 6000;
      var timer = setInterval(function () { go(index + 1); }, interval);
      ['pointerdown', 'mouseenter', 'focusin'].forEach(function (ev) {
        root.addEventListener(ev, function () { clearInterval(timer); }, { once: true });
      });
    }
  }
  function boot() { document.querySelectorAll('[data-slider]').forEach(initSlider); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
